import React, { useEffect, useMemo, useRef, useState } from "react";
import { clearCoachBrowser } from "api/coachAuth";
import { startJwtSession } from "api/jwtSession";
import { theme } from "../../styles/theme";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/joy";
import FullPageContainer from "../container/FullPageContainer";
import QRCode from "react-qr-code";
import { LogoWithTextSvg } from "static/image/common/SvgAssets";
import { useQuery } from "@tanstack/react-query";
import {
  type CustomResponseFormat,
  getOneTimeTokenStatusDetail,
  issueOneTimeToken,
  type OneTimeTokenIssueResult,
  type OneTimeTokenStatusDetail,
} from "api";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "../../recoil/patientAtom";
import CustomModal from "../../components/common/CustomModal";
import { HOST_URL, isDev } from "../../config";
import mixpanel from "mixpanel-browser";

export default function PCLoginPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    data: ott,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["pcLogin", "ott"],
    queryFn: async () => {
      const res =
        (await issueOneTimeToken()) as unknown as CustomResponseFormat<OneTimeTokenIssueResult>;
      const payload = res?.result;
      const token = payload?.oneTimeToken;
      if (!token) {
        throw new Error("Failed to get one-time token");
      }
      return token;
    },
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();
  const setUser = useSetRecoilState(userAtom);
  const setPatientState = useSetRecoilState(patientAtom);

  const [isPolling, setIsPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(120);

  useEffect(() => {
    let pollTimer: any;
    let countdownTimer: any;
    let timeoutTimer: any;

    // 초기화
    setPollError(null);
    setTimedOut(false);
    setSecondsLeft(120);

    if (!ott) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    const checkStatus = async () => {
      try {
        const res = (await getOneTimeTokenStatusDetail(
          ott
        )) as unknown as CustomResponseFormat<OneTimeTokenStatusDetail>;

        const body = res?.result;
        const idToken = body?.idToken;
        const status = body?.status;

        if (status === "expired") {
          // Stop polling immediately and show timeout UI
          setTimedOut(true);
          setIsPolling(false);
          clearInterval(pollTimer);
          clearInterval(countdownTimer);
          clearTimeout(timeoutTimer);
          return;
        }

        const authenticated = status === "verified" && !!idToken;

        if (authenticated) {
          // Stop polling immediately to avoid duplicate login attempts
          clearInterval(pollTimer);
          clearInterval(countdownTimer);
          clearTimeout(timeoutTimer);
          setIsPolling(false);
          try {
            // PC 웹 세션은 JWT: /user/v2/login(type=web) 이 액세스 JWT(메모리) + 리프레시 쿠키(유휴 24h·절대 7일)를 준다.
            // 레거시 X-Auth-Token 을 localStorage 에 남기지 않는다. 앱 웹뷰의 레거시 경로와는 무관하다.
            // axiosInstance 를 쓰지 않는다 — 로그인 전 401 이면 인터셉터가 세션을 지우고 /login 으로 새로고침해
            // 이 화면의 오류 처리가 사라진다. fetch 로 직접 부르고 실패는 아래 catch 가 받는다.
            const res = await fetch(`${HOST_URL}/user/v2/login`, {
              method: "POST",
              credentials: "include", // 리프레시 쿠키 수신
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ type: "web", idToken }),
            });
            const body = (await res.json()) as CustomResponseFormat<{
              accessToken?: string;
            }>;
            const jwt =
              res.ok && body?.code === 0 ? body.result?.accessToken : undefined;
            if (!jwt)
              throw new Error(
                `web login failed: ${body?.message ?? res.status}`
              );

            const result = await startJwtSession(jwt, "pcweb");
            clearCoachBrowser(); // 유저 로그인 — 코치 브라우저 표식 제거

            if (result?.trackId) {
              mixpanel.identify(result.trackId);
            }

            setUser(result);
            if (result?.role === "patient" && result?.userId) {
              setPatientState({
                patientId: Number(`${result.userId}`),
                nickname: result.nickname,
              });
            }

            clearInterval(pollTimer);
            clearInterval(countdownTimer);
            clearTimeout(timeoutTimer);
            setIsPolling(false);
            navigate("/", { replace: true });
          } catch (err) {
            clearInterval(pollTimer);
            clearInterval(countdownTimer);
            clearTimeout(timeoutTimer);
            setIsPolling(false);
            setPollError("로그인에 실패했어요. 다시 시도해주세요.");
          }
        }
      } catch (e: any) {
        // 상태 미완료(404/409 등)는 에러로 떨어질 수 있으므로, 2xx가 아니면 무시하고 계속 폴링.
        // 네트워크 에러가 지속되면 타임아웃에서 종료.
      }
    };

    // 2초 간격 폴링 시작
    pollTimer = setInterval(checkStatus, 2000);
    // 즉시 1회 수행하여 반응 속도 향상
    checkStatus();

    // 카운트다운 (1초 간격)
    countdownTimer = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    // 2분 타임아웃
    timeoutTimer = setTimeout(() => {
      setTimedOut(true);
      setIsPolling(false);
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
    }, 120000);

    return () => {
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
      clearTimeout(timeoutTimer);
    };
  }, [ott]);

  // https://distancing.onelink.me/DeUR/6ltrqdb0
  const loginUrl = useMemo(() => {
    const token = ott ?? "";
    const base = isDev
      ? "https://distancing-dev.onelink.me/8jMV/a8qf4cey"
      : "https://distancing.onelink.me/DeUR/6ltrqdb0";
    const url = `${base}?deep_link_sub1=${encodeURIComponent(token)}`;
    console.log(url);
    return url;
  }, [ott]);

  return (
    <FullPageContainer
      hasFooter={false}
      backgroundColor={theme.vars.palette.Background.Level2}
    >
      <Box sx={{ position: "absolute", top: 20, left: 20 }}>
        <LogoWithTextSvg />
      </Box>
      <Stack
        ref={containerRef}
        flexGrow={1}
        justifyContent="center"
        alignItems="center"
        gap={2}
        sx={{ width: "100%", maxWidth: "660px", mx: "auto", px: "20px" }}
      >
        {isLoading ? (
          <Stack alignItems="center" gap={1}>
            <CircularProgress size="lg" color={"neutral"} />
            <Typography
              variant="Subtitle"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
              }}
            >
              QR 생성 중...
            </Typography>
          </Stack>
        ) : isError ? (
          <Stack alignItems="center" gap={1}>
            <Typography
              variant="Subtitle"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
              }}
            >
              QR 생성 중 오류가 발생했어요.
            </Typography>
            <Button size={"md"} variant="Solid" onClick={() => refetch()}>
              다시 시도
            </Button>
          </Stack>
        ) : (
          <Stack alignItems="center" gap={4}>
            <Box width={"160px"} height={"160px"}>
              <QRCode
                value={loginUrl}
                size={160}
                style={{ display: "block" }}
                aria-label="디스턴싱 로그인용 QR 코드"
                role="img"
              />
            </Box>
            <Stack spacing={1} alignItems="center">
              <Typography
                variant="H3"
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {"디스턴싱 앱으로 로그인"}
              </Typography>
              <Typography
                variant="Subtitle"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                }}
              >
                {"휴대전화로 QR 코드를 스캔해주세요."}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
      <CustomModal
        open={timedOut}
        onClose={() => {
          setTimedOut(false);
          refetch();
        }}
        title={"로그인 실패"}
        subtitle={"새 QR 코드를 생성할게요. 다시 시도해주세요."}
        actions={[
          {
            label: "확인",
            onClick: () => {
              setTimedOut(false);
              refetch();
            },
            variant: "Solid",
          },
        ]}
      />
    </FullPageContainer>
  );
}
